import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { findPost, BLOG_POSTS } from "../lib/blog";

const SITE = "https://riznwash.com";
const EASE = [0.22, 1, 0.36, 1];

function Block({ block }) {
  if (block.type === "h2")
    return (
      <h2 className="font-display text-2xl md:text-3xl text-rw-ink mt-12 mb-4 leading-tight">
        {block.text}
      </h2>
    );
  if (block.type === "p")
    return (
      <p className="text-rw-ink2 text-lg leading-relaxed mt-4">{block.text}</p>
    );
  if (block.type === "ul")
    return (
      <ul className="text-rw-ink2 text-lg leading-relaxed mt-4 space-y-2 list-disc pl-6">
        {block.items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    );
  if (block.type === "quote")
    return (
      <blockquote className="mt-8 border-l-4 border-rw-blue pl-6 italic text-xl text-rw-ink leading-relaxed">
        {block.text}
      </blockquote>
    );
  if (block.type === "table") {
    return (
      <div className="mt-8 overflow-x-auto">
        <table className="min-w-full text-sm border border-rw-ink/10 rounded-xl overflow-hidden">
          <thead className="bg-rw-bg2 text-rw-ink3">
            <tr>
              {block.headers.map((h, i) => (
                <th
                  key={i}
                  className="text-left px-4 py-3 font-semibold border-b border-rw-ink/10"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? "" : "bg-rw-bg2/50"}>
                {row.map((cell, j) => (
                  <td
                    key={j}
                    className="px-4 py-3 border-b border-rw-ink/10 text-rw-ink2"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (block.type === "cta") {
    return (
      <motion.div
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.97 }}
        className="mt-10 inline-block"
      >
        <Link
          to={block.href}
          className="btn-dark rounded-full px-6 py-3 text-sm md:text-base font-semibold"
          data-testid="blog-cta"
        >
          {block.text} →
        </Link>
      </motion.div>
    );
  }
  return null;
}

export default function BlogArticle() {
  const { blogSlug } = useParams();
  const post = findPost(blogSlug);
  if (!post) return <Navigate to="/blog" replace />;

  const path = `/blog/${post.slug}`;
  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.description,
      image: post.hero,
      datePublished: post.published,
      dateModified: post.published,
      author: { "@type": "Organization", name: "RIZ N WASH" },
      publisher: {
        "@type": "Organization",
        name: "RIZ N WASH",
        logo: {
          "@type": "ImageObject",
          url: `${SITE}/logos/brand-logo.png`,
        },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}${path}` },
      articleSection: post.category,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "RIZ N WASH", item: SITE },
        {
          "@type": "ListItem",
          position: 2,
          name: "Journal",
          item: `${SITE}/blog`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: post.title,
          item: `${SITE}${path}`,
        },
      ],
    },
  ];

  return (
    <div
      className="min-h-screen bg-rw-bg text-rw-ink"
      data-testid={`blog-${post.slug}`}
    >
      <Seo
        title={post.title}
        description={post.description}
        path={path}
        jsonLd={jsonLd}
        image={post.hero}
      />
      <Nav />

      <article className="max-w-3xl mx-auto px-6 md:px-10 pt-10 md:pt-20 pb-16">
        <nav
          className="overline mb-4 flex flex-wrap items-center gap-2 text-rw-ink3"
          aria-label="Breadcrumb"
        >
          <Link to="/" className="hover:text-rw-ink">
            Home
          </Link>
          <span aria-hidden>·</span>
          <Link to="/blog" className="hover:text-rw-ink">
            Journal
          </Link>
        </nav>
        <div className="text-sm text-rw-ink3 mt-6">
          {post.category} · {post.read} ·{" "}
          {new Date(post.published).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="font-display font-light text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.05] mt-3"
          data-testid="blog-h1"
        >
          {post.title}
        </motion.h1>
        <p className="mt-4 text-lg text-rw-ink2 leading-relaxed">
          {post.description}
        </p>
      </article>

      <div className="max-w-3xl mx-auto px-6 md:px-10 pb-8">
        <img
          src={post.hero}
          alt={post.title}
          className="w-full aspect-[16/9] object-cover rounded-2xl"
        />
      </div>

      <article className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        {post.body.map((b, i) => (
          <Block key={i} block={b} />
        ))}
      </article>

      <section className="bg-rw-bg2 border-t border-rw-ink/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
          <div className="overline mb-3">Keep reading</div>
          <h3 className="font-display text-2xl md:text-3xl mb-6">
            More from the journal
          </h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group block rounded-2xl border border-rw-ink/10 bg-rw-bg p-6 hover:border-rw-blue transition-all"
                data-testid={`related-blog-${p.slug}`}
              >
                <div className="overline text-rw-ink3">
                  {p.category} · {p.read}
                </div>
                <div className="font-display text-lg mt-1 group-hover:text-rw-blue transition-colors leading-tight">
                  {p.title}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
